import React from 'react';
import styled from 'styled-components';

// Styled components for different text styles
const TextContainer = styled.div<{
  variant?: 'info' | 'warning' | 'success' | 'error' | 'default';
  size?: 'small' | 'medium' | 'large';
  weight?: 'normal' | 'bold' | 'semibold';
  align?: 'left' | 'center' | 'right';
  padding?: string;
  margin?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: string;
  customStyles?: React.CSSProperties;
}>`
  padding: ${({ padding }) => padding || '12px 16px'};
  margin: ${({ margin }) => margin || '8px 0'};
  border-radius: ${({ borderRadius }) => borderRadius || '6px'};
  text-align: ${({ align }) => align || 'left'};
  font-weight: ${({ weight }) => {
    switch (weight) {
      case 'bold':
        return '700';
      case 'semibold':
        return '600';
      default:
        return '400';
    }
  }};
  font-size: ${({ size }) => {
    switch (size) {
      case 'small':
        return '12px';
      case 'large':
        return '18px';
      default:
        return '14px';
    }
  }};
  line-height: 1.5;

  /* Variant-based styling */
  background-color: ${({ theme, variant, backgroundColor }) => {
    if (backgroundColor) return backgroundColor;
    switch (variant) {
      case 'info':
        return `${theme.colors.secondary}1a`;
      case 'warning':
        return `${theme.colors.warning}1a`;
      case 'success':
        return `${theme.colors.success}1a`;
      case 'error':
        return `${theme.colors.error}1a`;
      default:
        return theme.colors.backgroundHover;
    }
  }};

  border: 1px solid
    ${({ theme, variant, borderColor }) => {
      if (borderColor) return borderColor;
      switch (variant) {
        case 'info':
          return theme.colors.secondary;
        case 'warning':
          return theme.colors.warning;
        case 'success':
          return theme.colors.success;
        case 'error':
          return theme.colors.error;
        default:
          return theme.colors.border.light;
      }
    }};

  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'info':
        return theme.colors.secondary;
      case 'warning':
        return theme.colors.text.primary;
      case 'success':
        return theme.colors.success;
      case 'error':
        return theme.colors.error;
      default:
        return theme.colors.text.primary;
    }
  }};

  /* Apply custom styles */
  ${({ customStyles }) =>
    customStyles &&
    Object.entries(customStyles)
      .map(([key, value]) => `${key}: ${value};`)
      .join('')}
`;

// Icon component for different variants
const Icon = styled.span<{ variant?: string }>`
  margin-right: 8px;
  font-size: 16px;
`;

const Content = styled.div<{ $preserveLineBreaks?: boolean }>`
  ${({ $preserveLineBreaks }) =>
    $preserveLineBreaks ? 'white-space: pre-line;' : ''}
`;

interface CustomTextDisplayProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style'> {
  text: React.ReactNode;
  variant?: 'info' | 'warning' | 'success' | 'error' | 'default';
  size?: 'small' | 'medium' | 'large';
  weight?: 'normal' | 'bold' | 'semibold';
  align?: 'left' | 'center' | 'right';
  padding?: string;
  margin?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: string;
  showIcon?: boolean;
  customStyles?: React.CSSProperties;
}

export const CustomTextDisplay: React.FC<CustomTextDisplayProps> = ({
  text,
  variant = 'default',
  size = 'medium',
  weight = 'normal',
  align = 'left',
  padding,
  margin,
  backgroundColor,
  borderColor,
  borderRadius,
  showIcon = false,
  customStyles,
  className,
  ...rest
}) => {
  const getIcon = () => {
    if (!showIcon) return null;

    switch (variant) {
      case 'info':
        return 'ℹ️';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return null;
    }
  };

  return (
    <TextContainer
      variant={variant}
      size={size}
      weight={weight}
      align={align}
      padding={padding}
      margin={margin}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderRadius={borderRadius}
      customStyles={customStyles}
      className={className}
      {...rest}
    >
      <Content $preserveLineBreaks={typeof text === 'string'}>
        {getIcon() && <Icon variant={variant}>{getIcon()}</Icon>} {text}
      </Content>
    </TextContainer>
  );
};

// Pre-configured components for common use cases
export const InfoText: React.FC<Omit<CustomTextDisplayProps, 'variant'>> = (
  props
) => <CustomTextDisplay {...props} variant="info" showIcon />;

export const WarningText: React.FC<Omit<CustomTextDisplayProps, 'variant'>> = (
  props
) => <CustomTextDisplay {...props} variant="warning" showIcon />;

export const SuccessText: React.FC<Omit<CustomTextDisplayProps, 'variant'>> = (
  props
) => <CustomTextDisplay {...props} variant="success" showIcon />;

export const ErrorText: React.FC<Omit<CustomTextDisplayProps, 'variant'>> = (
  props
) => <CustomTextDisplay {...props} variant="error" showIcon />;

export const HelpText: React.FC<
  Omit<CustomTextDisplayProps, 'variant' | 'size'>
> = (props) => <CustomTextDisplay {...props} variant="info" size="small" />;

export const SectionHeader: React.FC<
  Omit<CustomTextDisplayProps, 'variant' | 'size' | 'weight'>
> = (props) => (
  <CustomTextDisplay {...props} variant="default" size="large" weight="bold" />
);
